'use client';

import dynamic from 'next/dynamic';
import React, { useEffect } from 'react';
import { ProfileNoLoyaltyWallContainerProps } from '@/components/blocks/profile-no-loyalty-wall/profile-no-loyalty-wall.container';
import { TripChecklistContainerProps } from '@/components/blocks/trip-checklist/trip-checklist.container';
import { AddOnProductsProps } from '@/components/modules/add-on-products/add-on-products';
import { AltapayPaymentProps } from '@/components/modules/altapay-payment/altapay-payment';
import { AnimatedBannerProps } from '@/components/modules/animated-banner/animated-banner';
import { BookingFlowProps } from '@/components/modules/booking-flow/booking-flow';
import { BookingOverviewProps } from '@/components/modules/booking-overview/booking-overview';
import { BookingReceiptProps } from '@/components/modules/booking-receipt/booking-receipt';
import { BrandTeasersProps } from '@/components/modules/brand-teasers/brand-teasers';
import { CardBannerProps } from '@/components/modules/card-banner/card-banner';
import { ComposableContentProps } from '@/components/modules/composable-content/composable-content';
import { ConfigurationFlowProps } from '@/components/modules/configuration-flow/configuration-flow';
import { CountryMapProps } from '@/components/modules/country-map/country-map';
import { CustomHtmlProps } from '@/components/modules/custom-html/custom-html';
import { CustomProductListProps } from '@/components/modules/custom-product-list/custom-product-list';
import { DateAndPricesProps } from '@/components/modules/date-and-prices/date-and-prices';
import { DestinationFactsProps } from '@/components/modules/destination-facts/destination-facts';
import { DestinationFactsManualProps } from '@/components/modules/destination-facts-manual/destination-facts-manual';
import { DestinationIntroductionProps } from '@/components/modules/destination-introduction/destination-introduction';
import { DestinationMapProps } from '@/components/modules/destination-map/destination-map';
import { ErrorMessageProps } from '@/components/modules/error-message/error-message';
import { ExtrasProps } from '@/components/modules/extras/extras';
import { FullTeaserProps } from '@/components/modules/full-teaser/full-teaser';
import { HeaderCarouselProps } from '@/components/modules/header-carousel/header-carousel';
import { HighlightedExtraProps } from '@/components/modules/highlighted-extra/highlighted-extra';
import { HotelFacilitiesProps } from '@/components/modules/hotel-facilities/hotel-facilities';
import { HotelHeroProps } from '@/components/modules/hotel-hero/hotel-hero';
import { HotelMapProps } from '@/components/modules/hotel-map/hotel-map';
import { HubspotFormProps } from '@/components/modules/hubspot-form/hubspot-form';
import { ImageAndVideoGridProps } from '@/components/modules/image-and-video-grid/image-and-video-grid';
import { ImageScrollOverProps } from '@/components/modules/image-scroll-over/image-scroll-over';
import { InPageNavigationProps } from '@/components/modules/in-page-navigation/in-page-navigation';
import { InclusionsListProps } from '@/components/modules/inclusions-list/inclusions-list';
import { InformationCarouselProps } from '@/components/modules/information-carousel/information-carousel';
import { InspirationalSearchEntryPointModuleProps } from '@/components/modules/inspirational-search-entry-point-module/inspirational-search-entry-point-module';
import { InspirationalSearchFlowProps } from '@/components/modules/inspirational-search-flow/inspirational-search-flow';
import { InspirationalSearchResultProps } from '@/components/modules/inspirational-search-result/inspirational-search-result';
import { MinimalHeaderProps } from '@/components/modules/minimal-header/minimal-header';
import ModuleLoadingFallbackComponent from '@/components/modules/module-loading-fallback/module-loading-fallback';
import { NumberedSectionsProps } from '@/components/modules/numbered-sections/numbered-sections';
import { OnboardingFlowModuleProps } from '@/components/modules/onboarding-flow-module/onboarding-flow-module';
import { QuickLinksProps } from '@/components/modules/quick-links/quick-links';
import { RecentSearchesProps } from '@/components/modules/recent-searches/recent-searches';
import { RoomDescriptionProps } from '@/components/modules/room-description/room-description';
import { SearchByThemeProps } from '@/components/modules/search-by-theme/search-by-theme';
import { SecondaryHeaderProps } from '@/components/modules/secondary-header/secondary-header';
import { SignInSignUpFlowModuleProps } from '@/components/modules/sign-in-sign-up-flow-module/sign-in-sign-up-flow-module';
import { SlopeMapProps } from '@/components/modules/slope-map/slope-map';
import { SpotProps } from '@/components/modules/spot/spot';
import { StackAndTabsProps } from '@/components/modules/stack-and-tabs/stack-and-tabs';
import { TeaserGridProps } from '@/components/modules/teaser-grid/teaser-grid';
import { TravelPlanModuleProps } from '@/components/modules/travel-plan-module/travel-plan-module';
import { TripRouteDetailsProps } from '@/components/modules/trip-route-details/trip-route-details';
import ModuleLockWrapper from '@/components/utilities/module-lock-wrapper/module-lock-wrapper';
import {
  getNavigationTheme,
  setNavigationTheme,
} from '@/libraries/hooks/use-scroll-theme';

const createModuleLoadingFallback = ({
  displayName,
  height,
}: {
  displayName: string;
  height?: string;
}) => {
  const ModuleLoadingFallback = () => (
    <ModuleLoadingFallbackComponent height={height} />
  );

  ModuleLoadingFallback.displayName = displayName;

  return ModuleLoadingFallback;
};

const ModuleLoadingFallback = createModuleLoadingFallback({
  displayName: 'ModuleLoadingFallback',
});

const AddOnProducts = dynamic(
  () => import('@/components/modules/add-on-products/add-on-products'),
);
const AltapayPayment = dynamic(
  () => import('@/components/modules/altapay-payment/altapay-payment'),
);
const AnimatedBanner = dynamic(
  () => import('@/components/modules/animated-banner/animated-banner'),
);
const BookingFlowModule = dynamic(
  () => import('@/components/modules/booking-flow/booking-flow'),
);
const BookingOverviewModule = dynamic(
  () => import('@/components/modules/booking-overview/booking-overview'),
);
const BookingReceiptModule = dynamic(
  () => import('@/components/modules/booking-receipt/booking-receipt'),
);
const BrandTeasers = dynamic(
  () => import('@/components/modules/brand-teasers/brand-teasers'),
);
const ConfigurationFlowModule = dynamic(
  () => import('@/components/modules/configuration-flow/configuration-flow'),
);
const CardBanner = dynamic(
  () => import('@/components/modules/card-banner/card-banner'),
);
const ComposableContent = dynamic(
  () => import('@/components/modules/composable-content/composable-content'),
);
const CountryMap = dynamic(
  () => import('@/components/modules/country-map/country-map'),
);
const CustomHtml = dynamic(
  () => import('@/components/modules/custom-html/custom-html'),
);
const CustomProductList = dynamic(
  () => import('@/components/modules/custom-product-list/custom-product-list'),
);
const DateAndPrices = dynamic(
  () => import('@/components/modules/date-and-prices/date-and-prices'),
);
const DestinationFactsManual = dynamic(
  () =>
    import(
      '@/components/modules/destination-facts-manual/destination-facts-manual'
    ),
);
const DestinationFacts = dynamic(
  () => import('@/components/modules/destination-facts/destination-facts'),
);
const DestinationIntroduction = dynamic(
  () =>
    import(
      '@/components/modules/destination-introduction/destination-introduction'
    ),
);
const DestinationMap = dynamic(
  () => import('@/components/modules/destination-map/destination-map'),
);
const ErrorMessage = dynamic(
  () => import('@/components/modules/error-message/error-message'),
);
const Extras = dynamic(() => import('@/components/modules/extras/extras'));
const FullTeaser = dynamic(
  () => import('@/components/modules/full-teaser/full-teaser'),
);
const HeaderCarousel = dynamic(
  () => import('@/components/modules/header-carousel/header-carousel'),
);
const HighlightedExtra = dynamic(
  () => import('@/components/modules/highlighted-extra/highlighted-extra'),
);
const HotelFacilities = dynamic(
  () => import('@/components/modules/hotel-facilities/hotel-facilities'),
);
const HotelHero = dynamic(
  () => import('@/components/modules/hotel-hero/hotel-hero'),
);
const HotelMap = dynamic(
  () => import('@/components/modules/hotel-map/hotel-map'),
);
const HubspotForm = dynamic(
  () => import('@/components/modules/hubspot-form/hubspot-form'),
  { loading: ModuleLoadingFallback },
);
const ImageAndVideoGrid = dynamic(
  () =>
    import('@/components/modules/image-and-video-grid/image-and-video-grid'),
);
const ImageScrollOver = dynamic(
  () => import('@/components/modules/image-scroll-over/image-scroll-over'),
);
const InPageNavigation = dynamic(
  () => import('@/components/modules/in-page-navigation/in-page-navigation'),
);
const InclusionsList = dynamic(
  () => import('@/components/modules/inclusions-list/inclusions-list'),
);
const InformationCarousel = dynamic(
  () =>
    import('@/components/modules/information-carousel/information-carousel'),
);

const InspirationalSearchEntryPointModule = dynamic(
  () =>
    import(
      '@/components/modules/inspirational-search-entry-point-module/inspirational-search-entry-point-module'
    ),
);
const InspirationalSearchFlow = dynamic(
  () =>
    import(
      '@/components/modules/inspirational-search-flow/inspirational-search-flow'
    ),
);
const InspirationalSearchResult = dynamic(
  () =>
    import(
      '@/components/modules/inspirational-search-result/inspirational-search-result'
    ),
);
const MinimalHeader = dynamic(
  () => import('@/components/modules/minimal-header/minimal-header'),
);
const NumberedSections = dynamic(
  () => import('@/components/modules/numbered-sections/numbered-sections'),
);
const OnboardingFlowModule = dynamic(
  () =>
    import(
      '@/components/modules/onboarding-flow-module/onboarding-flow-module'
    ),
);
const QuickLinks = dynamic(
  () => import('@/components/modules/quick-links/quick-links'),
);
const ProfileNoLoyaltyWallContainer = dynamic(
  () =>
    import(
      '@/components/blocks/profile-no-loyalty-wall/profile-no-loyalty-wall.container'
    ),
);
const RecentSearches = dynamic(
  () => import('@/components/modules/recent-searches/recent-searches'),
);
const RoomDescription = dynamic(
  () => import('@/components/modules/room-description/room-description'),
);
const SearchByTheme = dynamic(
  () => import('@/components/modules/search-by-theme/search-by-theme'),
);
const SecondaryHeader = dynamic(
  () => import('@/components/modules/secondary-header/secondary-header'),
);
const SignInSignUpFlowModule = dynamic(
  () =>
    import(
      '@/components/modules/sign-in-sign-up-flow-module/sign-in-sign-up-flow-module'
    ),
);
const SlopeMap = dynamic(
  () => import('@/components/modules/slope-map/slope-map'),
);
const Spot = dynamic(() => import('@/components/modules/spot/spot'));
const StackAndTabs = dynamic(
  () => import('@/components/modules/stack-and-tabs/stack-and-tabs'),
);
const TeaserGrid = dynamic(
  () => import('@/components/modules/teaser-grid/teaser-grid'),
);
const TravelPlanModule = dynamic(
  () => import('@/components/modules/travel-plan-module/travel-plan-module'),
);
const TripChecklistContainer = dynamic(
  () => import('@/components/blocks/trip-checklist/trip-checklist.container'),
);
const TripRouteDetails = dynamic(
  () => import('@/components/modules/trip-route-details/trip-route-details'),
);

export type Module =
  | ({
      $type: 'AltapayPaymentModuleDto';
    } & AltapayPaymentProps)
  | ({
      $type: 'AddOnProductsModuleDto';
    } & AddOnProductsProps)
  | ({
      $type: 'AnimatedBannerModuleDto';
    } & AnimatedBannerProps)
  | ({
      $type: 'BookingReceiptModuleDto';
    } & BookingReceiptProps)
  | ({
      $type: 'BookingFlowModuleDto';
    } & BookingFlowProps)
  | ({
      $type: 'BrandTeasersModuleDto';
    } & BrandTeasersProps)
  | ({
      $type: 'ConfigurationFlowModuleDto';
    } & ConfigurationFlowProps)
  | ({
      $type: 'CardBannerModuleDto';
    } & CardBannerProps)
  | ({
      $type: 'ComposableContentModuleDto';
    } & ComposableContentProps)
  | ({
      $type: 'CountryMapModuleDto';
    } & CountryMapProps)
  | ({
      $type: 'CustomHtmlModuleDto';
    } & CustomHtmlProps)
  | ({
      $type: 'CustomProductListModuleDto';
    } & CustomProductListProps)
  | ({
      $type: 'DateAndPricesModuleDto';
    } & DateAndPricesProps)
  | ({
      $type: 'DayToDayDescriptionAndRouteMapModuleDto';
    } & TripRouteDetailsProps)
  | ({
      $type: 'DestinationFactsManualModuleDto';
    } & DestinationFactsManualProps)
  | ({
      $type: 'DestinationFactsModuleDto';
    } & DestinationFactsProps)
  | ({
      $type: 'DestinationIntroductionModuleDto';
    } & DestinationIntroductionProps)
  | ({
      $type: 'DestinationMapModuleDto';
    } & DestinationMapProps)
  | ({
      $type: 'ErrorMessageModuleDto';
    } & ErrorMessageProps)
  | ({
      $type: 'ExtraModuleDto';
    } & ExtrasProps)
  | ({
      $type: 'FullTeaserModuleDto';
    } & FullTeaserProps)
  | ({
      $type: 'HeaderCarouselModuleDto';
    } & HeaderCarouselProps)
  | ({
      $type: 'HighlightedExtraModuleDto';
    } & HighlightedExtraProps)
  | ({
      $type: 'HotelFacilitiesModuleDto';
    } & HotelFacilitiesProps)
  | ({
      $type: 'HotelMapModuleDto';
    } & HotelMapProps)
  | ({
      $type: 'HotelPageHeroModuleDto';
    } & HotelHeroProps)
  | ({
      $type: 'HotelPageOverviewModuleDto';
    } & BookingOverviewProps)
  | ({
      $type: 'HubspotFormModuleDto';
    } & HubspotFormProps)
  | ({
      $type: 'ImageAndVideoGridModuleDto';
    } & ImageAndVideoGridProps)
  | ({
      $type: 'ImageScrollOverModuleDto';
    } & ImageScrollOverProps)
  | ({
      $type: 'IncludedExcludedModuleDto';
    } & InclusionsListProps)
  | ({
      $type: 'InformationCarouselModuleDto';
    } & InformationCarouselProps)
  | ({
      $type: 'InPageNavigation';
    } & InPageNavigationProps)
  | ({
      $type: 'InspirationalSearchEntryPointModuleDto';
    } & InspirationalSearchEntryPointModuleProps)
  | ({
      $type: 'InspirationalSearchFlowModuleDto';
    } & InspirationalSearchFlowProps)
  | ({
      $type: 'InspirationalSearchResultModuleDto';
    } & InspirationalSearchResultProps)
  | ({
      $type: 'MediaHeaderWithSearchModuleDto';
    } & SecondaryHeaderProps)
  | ({
      $type: 'MinimalHeaderModuleDto';
    } & MinimalHeaderProps)
  | ({
      $type: 'NumberedSectionsModuleDto';
    } & NumberedSectionsProps)
  | ({
      $type: 'OnboardingFlowModuleDto';
    } & OnboardingFlowModuleProps)
  | ({
      $type: 'ProfileNoLoyaltyWallHeadlessModuleDto';
    } & ProfileNoLoyaltyWallContainerProps)
  | ({
      $type: 'QuickLinksModuleDto';
    } & QuickLinksProps)
  | ({
      $type: 'RecentSearchesModuleDto';
    } & RecentSearchesProps)
  | ({
      $type: 'RoomDescriptionModuleDto';
    } & RoomDescriptionProps)
  | ({
      $type: 'SearchByThemeModuleDto';
    } & SearchByThemeProps)
  | ({
      $type: 'SecondaryHeaderModuleDto';
    } & SecondaryHeaderProps)
  | ({
      $type: 'SignInSignUpFlowModuleDto';
    } & SignInSignUpFlowModuleProps)
  | ({
      $type: 'SlopeMapModuleDto';
    } & SlopeMapProps)
  | ({
      $type: 'SpotModuleDto';
    } & SpotProps)
  | ({
      $type: 'StackAndTabsModuleDto';
    } & StackAndTabsProps)
  | ({
      $type: 'TeaserGridModuleDto';
    } & TeaserGridProps)
  | ({
      $type: 'TripChecklistHeadlessModuleDto';
    } & TripChecklistContainerProps)
  | ({
      $type: 'TravelPlanModuleDto';
    } & TravelPlanModuleProps);

const ModuleMap: {
  [key in Module['$type']]: React.ComponentType<any>;
} = {
  AddOnProductsModuleDto: AddOnProducts,
  AltapayPaymentModuleDto: AltapayPayment,
  AnimatedBannerModuleDto: AnimatedBanner,
  BookingFlowModuleDto: BookingFlowModule,
  BookingReceiptModuleDto: BookingReceiptModule,
  BrandTeasersModuleDto: BrandTeasers,
  CardBannerModuleDto: CardBanner,
  ComposableContentModuleDto: ComposableContent,
  ConfigurationFlowModuleDto: ConfigurationFlowModule,
  CountryMapModuleDto: CountryMap,
  CustomHtmlModuleDto: CustomHtml,
  CustomProductListModuleDto: CustomProductList,
  DateAndPricesModuleDto: DateAndPrices,
  DayToDayDescriptionAndRouteMapModuleDto: TripRouteDetails,
  DestinationFactsManualModuleDto: DestinationFactsManual,
  DestinationFactsModuleDto: DestinationFacts,
  DestinationIntroductionModuleDto: DestinationIntroduction,
  DestinationMapModuleDto: DestinationMap,
  ErrorMessageModuleDto: ErrorMessage,
  ExtraModuleDto: Extras,
  FullTeaserModuleDto: FullTeaser,
  HeaderCarouselModuleDto: HeaderCarousel,
  HighlightedExtraModuleDto: HighlightedExtra,
  HotelFacilitiesModuleDto: HotelFacilities,
  HotelMapModuleDto: HotelMap,
  HotelPageHeroModuleDto: HotelHero,
  HotelPageOverviewModuleDto: BookingOverviewModule,
  HubspotFormModuleDto: HubspotForm,
  ImageAndVideoGridModuleDto: ImageAndVideoGrid,
  ImageScrollOverModuleDto: ImageScrollOver,
  IncludedExcludedModuleDto: InclusionsList,
  InformationCarouselModuleDto: InformationCarousel,
  InPageNavigation,
  InspirationalSearchEntryPointModuleDto: InspirationalSearchEntryPointModule,
  InspirationalSearchFlowModuleDto: InspirationalSearchFlow,
  InspirationalSearchResultModuleDto: InspirationalSearchResult,
  MediaHeaderWithSearchModuleDto: SecondaryHeader,
  MinimalHeaderModuleDto: MinimalHeader,
  NumberedSectionsModuleDto: NumberedSections,
  OnboardingFlowModuleDto: OnboardingFlowModule,
  ProfileNoLoyaltyWallHeadlessModuleDto: ProfileNoLoyaltyWallContainer,
  QuickLinksModuleDto: QuickLinks,
  RecentSearchesModuleDto: RecentSearches,
  RoomDescriptionModuleDto: RoomDescription,
  SearchByThemeModuleDto: SearchByTheme,
  SecondaryHeaderModuleDto: SecondaryHeader,
  SignInSignUpFlowModuleDto: SignInSignUpFlowModule,
  SlopeMapModuleDto: SlopeMap,
  SpotModuleDto: Spot,
  StackAndTabsModuleDto: StackAndTabs,
  TeaserGridModuleDto: TeaserGrid,
  TravelPlanModuleDto: TravelPlanModule,
  TripChecklistHeadlessModuleDto: TripChecklistContainer,
};

function getModule(
  name: Module['$type'],
  props: Module,
): {
  component: React.ComponentType<Module>;
  props: Module;
} {
  const Component = ModuleMap[name];

  return { component: Component, props };
}

export interface ModuleListProps {
  /** An array of the modules to show */
  modules: Module[];
}

const ModuleList = ({ modules = [] }: ModuleListProps) => {
  useEffect(() => {
    if (!modules || modules.length < 1) return;

    const theme = modules?.[0]?.settings?.theme ?? 'light';

    if (!getNavigationTheme() && theme) {
      setNavigationTheme(theme);
    }
  }, [modules]);

  return (
    <React.Fragment>
      {modules.map((module, moduleIndex) => {
        const { component: Component, props } = getModule(module.$type, module);

        if (!Component) return null;

        return (
          <ModuleLockWrapper
            key={`${module.$type}-${moduleIndex}`}
            module={module}
            modules={modules}
          >
            <Component
              {...props}
              isFirst={moduleIndex === 0}
              setFooterBackground={moduleIndex === modules.length - 1}
            />
          </ModuleLockWrapper>
        );
      })}
    </React.Fragment>
  );
};

ModuleList.displayName = 'ModuleList';

export default ModuleList;
